import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MuiLink from '@mui/material/Link';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import React, { useMemo, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { CreateShipmentForm } from './CreateShipmentForm';
import { LinkShipmentPopover } from './LinkShipmentPopover';
import { cellSx, headerSx, btnSx, formatDate } from './PartsShipmentsHelpers';
import { ShipmentsPanelProps, LinkedShipment } from './PartsShipmentsPanel.props';
import { PostCreateShippingMethod } from './PostCreateShippingMethod';
import { ActionPermissions } from '../../../../../@types';
import { Shipment } from '../../../../../@types/shipment.type';
import { Panel } from '../../../../../components/atoms/Panel';
import { isUserHasPermission } from '../../../../../configs/permissions';
import { useGetShipmentServiceAllocationList } from '../../../../../queries/shipment-query';

type CreateMode = 'closed' | 'create' | 'shipping';

export const PartsShipmentsPanel: React.FC<ShipmentsPanelProps> = ({
  serviceId = 0,
  clientHierarchy,
  clientContractIds,
  clientName,
  contractName,
  destinationNodeId,
  destinationName,
  mainLocationNodeId,
  subLocationNodeId,
  primaryAllocationNodeId,
  secondaryAllocationNodeId,
  permissions,
}) => {
  const linkRef = useRef<HTMLButtonElement>(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [createMode, setCreateMode] = useState<CreateMode>('closed');
  const [createdShipment, setCreatedShipment] = useState<Shipment | null>(null);

  const closeCreateFlow = () => {
    setCreateMode('closed');
    setCreatedShipment(null);
  };

  const canDispatch = isUserHasPermission(ActionPermissions.Shipment_View_Dispatch, permissions);

  const allocationsQuery = useGetShipmentServiceAllocationList(serviceId);
  const shipments = useMemo<LinkedShipment[]>(
    () =>
      (allocationsQuery.data ?? []).map((a) => ({
        id: a.shipmentId,
        createdAt: a.createdAt,
        source: a.source,
        destination: a.destination,
        createdBy: a.createdBy,
      })),
    [allocationsQuery.data]
  );

  return (
    <Panel title="Shipments">
      {shipments.length > 0 ? (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headerSx}>Shipment ID</TableCell>
                <TableCell sx={headerSx}>Created</TableCell>
                <TableCell sx={headerSx}>Source</TableCell>
                <TableCell sx={headerSx}>Destination</TableCell>
                <TableCell sx={headerSx}>Created By</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {shipments.map((s, idx) => (
                <TableRow
                  key={String(s.id ?? idx)}
                  sx={{
                    bgcolor: idx % 2 === 0 ? 'transparent' : 'v4.tableEven',
                    '&:hover': { bgcolor: 'v4.tableHover' },
                  }}
                >
                  <TableCell sx={cellSx}>
                    <MuiLink
                      component={RouterLink}
                      to={`/shipments/${s.id}`}
                      underline="hover"
                      sx={{ fontSize: '12px', fontWeight: 500 }}
                    >
                      {String(s.id)}
                    </MuiLink>
                  </TableCell>
                  <TableCell sx={{ ...cellSx, whiteSpace: 'nowrap' }}>
                    {formatDate(s.createdAt)}
                  </TableCell>
                  <TableCell sx={cellSx}>{s.source ?? '-'}</TableCell>
                  <TableCell sx={cellSx}>{s.destination ?? '-'}</TableCell>
                  <TableCell sx={cellSx}>{s.createdBy ?? '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Typography sx={{ fontSize: '13px', color: 'text.secondary', py: 2, textAlign: 'center' }}>
          No linked shipments
        </Typography>
      )}

      {createMode === 'closed' && canDispatch && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
          <Button
            size="small"
            variant="contained"
            onClick={() => setCreateMode('create')}
            sx={btnSx}
          >
            Create New Shipment
          </Button>
          <Button
            ref={linkRef}
            size="small"
            variant="contained"
            onClick={() => setLinkOpen(true)}
            sx={btnSx}
          >
            Link Existing Shipment
          </Button>
        </Box>
      )}

      {createMode === 'create' && (
        <CreateShipmentForm
          serviceId={serviceId}
          destinationNodeId={destinationNodeId}
          destinationName={destinationName}
          mainLocationNodeId={mainLocationNodeId}
          subLocationNodeId={subLocationNodeId}
          primaryAllocationNodeId={primaryAllocationNodeId}
          secondaryAllocationNodeId={secondaryAllocationNodeId}
          clientHierarchy={clientHierarchy}
          clientContractIds={clientContractIds}
          clientName={clientName}
          contractName={contractName}
          permissions={permissions}
          onCreated={(shipment, action) => {
            if (action === 'connote') {
              setCreatedShipment(shipment);
              setCreateMode('shipping');
            } else {
              closeCreateFlow();
            }
          }}
          onCancel={closeCreateFlow}
        />
      )}

      {createMode === 'shipping' && createdShipment && (
        <PostCreateShippingMethod shipment={createdShipment} onClose={closeCreateFlow} />
      )}

      <LinkShipmentPopover
        anchorEl={linkRef.current}
        open={linkOpen}
        onClose={() => setLinkOpen(false)}
        serviceId={serviceId}
        clientHierarchy={clientHierarchy}
      />
    </Panel>
  );
};
